import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined"
import LoadingButton from "@mui/lab/LoadingButton"
import Box from "@mui/material/Box"
import Button from "@mui/material/Button"
import Chip from "@mui/material/Chip"
import Dialog from "@mui/material/Dialog"
import DialogActions from "@mui/material/DialogActions"
import DialogContent from "@mui/material/DialogContent"
import Skeleton from "@mui/material/Skeleton"
import Typography from "@mui/material/Typography"
import { useTheme } from "@mui/material/styles"
import { useCallback, useEffect, useMemo, useState } from "react"
import { useSelector } from "react-redux"
import { useBlocker } from "react-router-dom"

import CloseableSnackbar, {
  useCloseableSnackbarState,
} from "../../../common/components/CloseableSnackbar"
import Column from "../../../common/components/Column"
import Row from "../../../common/components/Row"
import { isAccountOwner, isConcierge } from "../../users/accountRole"
import { selectProfile } from "../../users/reducer"
import CampaignScriptEditor from "./CampaignScriptEditor"
import CampaignScriptPreviewDialog from "./CampaignScriptPreviewDialog"
import {
  SCRIPT_STATUS,
  SCRIPT_STATUS_LABELS,
  formatScriptTimestamp,
} from "./campaignScriptConstants"
import { useCampaignScript } from "./useCampaignScript"

function statusChipSx(theme, status) {
  const approved = status === SCRIPT_STATUS.approved
  const color = approved
    ? theme.palette.success.main
    : theme.palette.warning.dark
  const bg = approved
    ? theme.palette.success.light
    : theme.palette.warning.light
  return {
    height: 28,
    fontWeight: 800,
    letterSpacing: 0.6,
    textTransform: "uppercase",
    color,
    bgcolor: `${bg}55`,
    border: `1px solid ${color}55`,
  }
}

function formatEditorName(user) {
  return user?.name || user?.email || "Unknown"
}

export default function CampaignScriptSection() {
  const theme = useTheme()
  const profile = useSelector(selectProfile)
  const canApprovePermission = isAccountOwner(profile)
  const concierge = isConcierge(profile)
  const canEditScript = !concierge
  const accent = theme.palette.colors?.brand500 ?? theme.palette.primary.main
  const { isLoading, isSaving, isApproving, script, saveDraft, approveScript } =
    useCampaignScript()
  const [content, setContent] = useState("")
  const [loadedContent, setLoadedContent] = useState("")
  const [previewOpen, setPreviewOpen] = useState(false)
  const snackbar = useCloseableSnackbarState()

  useEffect(() => {
    const next = script.content || ""
    setContent(next)
    setLoadedContent(next)
  }, [script.content, script.updated_at, script.status])

  const isDirty = content !== loadedContent
  const displayedStatus =
    isDirty && script.status === SCRIPT_STATUS.approved
      ? SCRIPT_STATUS.awaitingApproval
      : script.status || SCRIPT_STATUS.awaitingApproval
  const busy = isSaving || isApproving
  const canApprove =
    canApprovePermission &&
    !busy &&
    (isDirty || displayedStatus !== SCRIPT_STATUS.approved)

  const shouldBlockNavigation = useCallback(
    ({ currentLocation, nextLocation }) => {
      return (
        isDirty &&
        (currentLocation.pathname !== nextLocation.pathname ||
          currentLocation.search !== nextLocation.search)
      )
    },
    [isDirty]
  )
  const navigationBlocker = useBlocker(shouldBlockNavigation)

  useEffect(() => {
    function handleBeforeUnload(event) {
      if (!isDirty) {
        return
      }
      event.preventDefault()
      event.returnValue = ""
    }
    window.addEventListener("beforeunload", handleBeforeUnload)
    return () => window.removeEventListener("beforeunload", handleBeforeUnload)
  }, [isDirty])

  const lastEditedLabel = useMemo(() => {
    const when = formatScriptTimestamp(script.updated_at)
    if (!when && !script.updated_by) {
      return null
    }
    return {
      name: formatEditorName(script.updated_by),
      when,
    }
  }, [script.updated_at, script.updated_by])

  function handleSaveError(error, fallback) {
    const detail =
      error?.response?.data?.detail ||
      error?.response?.data?.content ||
      fallback
    const message = Array.isArray(detail) ? detail[0] : detail
    snackbar.open(message || fallback, "error")
  }

  function handleSaveDraft() {
    return saveDraft(content)
      .then((payload) => {
        setLoadedContent(payload.content || "")
        setContent(payload.content || "")
        snackbar.open(payload.message || "Script saved successfully.")
      })
      .catch((error) => {
        handleSaveError(error, "Could not save the script. Please try again.")
      })
  }

  function handleApprove() {
    return approveScript(content)
      .then((payload) => {
        setLoadedContent(payload.content || "")
        setContent(payload.content || "")
        snackbar.open(
          payload.message || "Campaign script approved successfully."
        )
      })
      .catch((error) => {
        handleSaveError(
          error,
          "Could not approve the script. Please try again."
        )
      })
  }

  return (
    <Box
      component="section"
      aria-label="Campaign Script"
      sx={{
        width: "100%",
        borderRadius: 2,
        overflow: "hidden",
        bgcolor: "background.paper",
        border: `1px solid ${
          theme.palette.colors?.brand200 ?? theme.palette.divider
        }`,
        borderLeftWidth: 5,
        borderLeftColor: accent,
        boxShadow:
          "0 2px 12px -4px rgba(137, 47, 206, 0.14), 0 1px 4px -2px rgba(52, 64, 84, 0.06)",
      }}
    >
      <Box
        sx={{
          px: { xs: 2, sm: 2.5 },
          py: 2,
          borderBottom: `1px solid ${theme.palette.divider}`,
          background: `linear-gradient(135deg, ${
            theme.palette.colors?.brand50 ?? "#f8f1ff"
          }88 0%, rgba(255, 255, 255, 0.97) 70%)`,
        }}
      >
        <Row sx={{ alignItems: "flex-start", gap: 1.5 }}>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              width: 44,
              height: 44,
              borderRadius: 1.5,
              flexShrink: 0,
              color: accent,
              bgcolor: "background.paper",
              border: `1px solid ${theme.palette.divider}`,
            }}
          >
            <DescriptionOutlinedIcon />
          </Box>
          <Box sx={{ minWidth: 0, flex: 1 }}>
            <Typography variant="subtitle1" fontWeight={700}>
              Campaign Script
            </Typography>
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ mt: 0.5, lineHeight: 1.5, maxWidth: 640 }}
            >
              Edit your campaign calling script below.
              {canApprovePermission
                ? " Once you are satisfied, approve the script for your team to use."
                : " Save a draft — the account owner must approve it before the team can use it."}
            </Typography>
          </Box>
        </Row>
      </Box>

      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: { xs: "1fr", md: "minmax(0, 1fr) 280px" },
          gap: 2.5,
          p: { xs: 1.5, sm: 2 },
          alignItems: "start",
        }}
      >
        <Column sx={{ gap: 1.5, minWidth: 0 }}>
          {isLoading ? (
            <Skeleton variant="rounded" height={420} />
          ) : (
            <CampaignScriptEditor
              content={content}
              onChange={setContent}
              disabled={busy || !canEditScript}
            />
          )}
          {!canEditScript ? (
            <Typography variant="body2" color="text.secondary">
              Concierge users can view the account campaign script but cannot
              edit it. Ask the account owner to make changes.
            </Typography>
          ) : null}
          {!isLoading && !script.exists && !content ? (
            <Typography variant="body2" color="text.secondary">
              No campaign script has been added yet.
            </Typography>
          ) : null}
          <Row
            sx={{
              justifyContent: "space-between",
              alignItems: "center",
              gap: 1,
              flexWrap: "wrap",
            }}
          >
            <Typography variant="caption" color="text.secondary">
              {isDirty ? "Unsaved changes" : "All changes saved"}
              {!canApprovePermission ? " · Owner approval required" : ""}
            </Typography>
            <Row sx={{ gap: 1, flexWrap: "wrap" }}>
              <Button
                variant="outlined"
                disabled={busy}
                onClick={() => setPreviewOpen(true)}
                sx={{ textTransform: "none", fontWeight: 600 }}
              >
                Preview
              </Button>
              {canEditScript ? (
                <>
                  <LoadingButton
                    variant="outlined"
                    loading={isSaving}
                    disabled={busy || !isDirty}
                    onClick={handleSaveDraft}
                    sx={{ textTransform: "none", fontWeight: 600 }}
                  >
                    Save Draft
                  </LoadingButton>
                  {canApprovePermission ? (
                    <LoadingButton
                      variant="contained"
                      loading={isApproving}
                      disabled={!canApprove}
                      onClick={handleApprove}
                      sx={{ textTransform: "none", fontWeight: 600 }}
                    >
                      Approve Script
                    </LoadingButton>
                  ) : null}
                </>
              ) : null}
            </Row>
          </Row>
        </Column>

        <Column sx={{ gap: 2 }}>
          <Box
            sx={{
              borderRadius: 2,
              border: `1px solid ${theme.palette.divider}`,
              p: 2,
              bgcolor: "background.paper",
            }}
          >
            <Row
              sx={{
                alignItems: "center",
                justifyContent: "space-between",
                gap: 1,
              }}
            >
              <Typography
                variant="caption"
                color="text.secondary"
                fontWeight={700}
                sx={{ letterSpacing: 0.4, textTransform: "uppercase" }}
              >
                Script Status
              </Typography>
              {isLoading ? (
                <Skeleton width={88} height={28} />
              ) : (
                <Chip
                  size="small"
                  label={SCRIPT_STATUS_LABELS[displayedStatus]}
                  sx={{
                    flexShrink: 0,
                    ...statusChipSx(theme, displayedStatus),
                  }}
                />
              )}
            </Row>
            <Typography
              variant="caption"
              color="text.secondary"
              fontWeight={700}
              sx={{
                display: "block",
                mt: 2,
                letterSpacing: 0.4,
                textTransform: "uppercase",
              }}
            >
              Last edited by
            </Typography>
            {isLoading ? (
              <Skeleton width={140} />
            ) : lastEditedLabel ? (
              <>
                <Typography variant="body2" fontWeight={600} sx={{ mt: 0.5 }}>
                  {lastEditedLabel.name}
                </Typography>
                {lastEditedLabel.when ? (
                  <Typography variant="caption" color="text.secondary">
                    {lastEditedLabel.when}
                  </Typography>
                ) : null}
              </>
            ) : (
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ mt: 0.5 }}
              >
                Not edited yet
              </Typography>
            )}
          </Box>

          <Box
            sx={{
              borderRadius: 2,
              border: `1px solid ${theme.palette.divider}`,
              p: 2,
            }}
          >
            <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1.5 }}>
              Revision History
            </Typography>
            {isLoading ? (
              <Skeleton height={80} />
            ) : script.revisions?.length ? (
              <Column sx={{ gap: 1.5 }}>
                {script.revisions.map((revision) => (
                  <Box key={revision.id}>
                    <Typography variant="body2" fontWeight={600}>
                      {formatScriptTimestamp(revision.created_at) ||
                        "Unknown date"}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Edited by {formatEditorName(revision.edited_by)}
                      {revision.action === "approve" ? " · Approved" : ""}
                    </Typography>
                    {revision.is_current ? (
                      <Chip
                        label="Current"
                        size="small"
                        sx={{ mt: 0.5, height: 22, fontWeight: 700 }}
                      />
                    ) : null}
                  </Box>
                ))}
              </Column>
            ) : (
              <Typography variant="body2" color="text.secondary">
                No revisions yet.
              </Typography>
            )}
          </Box>
        </Column>
      </Box>

      <CampaignScriptPreviewDialog
        open={previewOpen}
        onClose={() => setPreviewOpen(false)}
        content={content}
      />
      <Dialog
        open={navigationBlocker.state === "blocked"}
        onClose={() => navigationBlocker.reset?.()}
        maxWidth="xs"
      >
        <DialogContent>
          <Typography variant="h6" sx={{ mb: 1 }}>
            Leave without saving?
          </Typography>
          <Typography variant="body2" color="text.secondary">
            You have unsaved script changes. They will be lost if you leave this
            page.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => navigationBlocker.proceed?.()}
            sx={{ textTransform: "none" }}
          >
            Leave
          </Button>
          <Button
            variant="contained"
            onClick={() => navigationBlocker.reset?.()}
            sx={{ textTransform: "none" }}
          >
            Stay
          </Button>
        </DialogActions>
      </Dialog>
      <CloseableSnackbar
        open={snackbar.isOpen}
        onClose={snackbar.close}
        message={snackbar.message}
        severity={snackbar.severity}
      />
    </Box>
  )
}
